import { useState, useRef, useEffect, useCallback } from 'react'

const getTouchDistance = (touch1, touch2) => {
  const dx = touch2.clientX - touch1.clientX
  const dy = touch2.clientY - touch1.clientY
  return Math.sqrt(dx * dx + dy * dy)
}

const getTouchCenter = (touch1, touch2) => ({
  x: (touch1.clientX + touch2.clientX) / 2,
  y: (touch1.clientY + touch2.clientY) / 2
})

export const usePinchZoom = (containerRef, deps = []) => {
  const [isPinching, setIsPinching] = useState(false)

  const stateRef = useRef({ scale: 0.45, position: { x: 0, y: 0 } })
  const gestureRef = useRef({
    initialDistance: 0,
    initialScale: 1,
    lastCenter: { x: 0, y: 0 }
  })
  const rafRef = useRef(null)

  const applyTransform = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const { scale, position } = stateRef.current
    el.style.transform = `scale(${scale}) translate(${position.x}px, ${position.y}px)`
  }, [containerRef])

  const scheduleApply = useCallback(() => {
    if (rafRef.current) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      applyTransform()
    })
  }, [applyTransform])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    applyTransform()

    const handleTouchStart = (e) => {
      if (e.touches.length !== 2) return
      e.preventDefault()

      const [touch1, touch2] = e.touches
      gestureRef.current = {
        initialDistance: getTouchDistance(touch1, touch2),
        initialScale: stateRef.current.scale,
        lastCenter: getTouchCenter(touch1, touch2)
      }

      setIsPinching(true)
    }

    const handleTouchMove = (e) => {
      if (e.touches.length !== 2) return
      e.preventDefault()

      const [touch1, touch2] = e.touches
      const { initialDistance, initialScale, lastCenter } = gestureRef.current
      const distance = getTouchDistance(touch1, touch2)
      const center = getTouchCenter(touch1, touch2)

      const newScale = Math.max(0.3, Math.min(3, initialScale * (distance / initialDistance)))
      stateRef.current.scale = newScale
      stateRef.current.position = {
        x: stateRef.current.position.x + (center.x - lastCenter.x) / newScale,
        y: stateRef.current.position.y + (center.y - lastCenter.y) / newScale
      }
      gestureRef.current.lastCenter = center

      scheduleApply()
    }

    const handleTouchEnd = (e) => {
      if (e.touches.length < 2) {
        setIsPinching(false)
      }
    }

    const handleWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()

      stateRef.current.scale = Math.max(
        0.3,
        Math.min(3, stateRef.current.scale + (e.deltaY > 0 ? -0.1 : 0.1))
      )
      scheduleApply()
    }

    const opts = { passive: false }
    container.addEventListener('touchstart', handleTouchStart, opts)
    container.addEventListener('touchmove', handleTouchMove, opts)
    container.addEventListener('touchend', handleTouchEnd, opts)
    container.addEventListener('wheel', handleWheel, opts)

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
      container.removeEventListener('wheel', handleWheel)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, deps)

  return { isPinching }
}
